/**
 * Grillion Header JavaScript
 * Handles cart bubble live updates, cart drawer toggling, and add-to-cart operations.
 */

window.Shopify = window.Shopify || {};

document.addEventListener('DOMContentLoaded', () => {
  // Get header section ID for Section Rendering API
  function getHeaderSectionId() {
    const drawerComp = document.querySelector('cart-items-component[data-drawer]') || document.querySelector('cart-items-component');
    if (drawerComp && drawerComp.dataset.sectionId) {
      return drawerComp.dataset.sectionId;
    }
    const sectionWrapper = document.querySelector('.grillion-header')?.closest('.shopify-section') || document.querySelector('cart-drawer-component')?.closest('.shopify-section');
    if (sectionWrapper && sectionWrapper.id) {
      return sectionWrapper.id.replace('shopify-section-', '');
    }
    return 'header_section';
  }

  // Open the native cart drawer dialog
  function openCartDrawer() {
    const nativeDrawer = document.querySelector('cart-drawer-component');
    if (nativeDrawer && typeof nativeDrawer.open === 'function') {
      nativeDrawer.open();
    } else if (nativeDrawer && typeof nativeDrawer.openDialog === 'function') {
      nativeDrawer.openDialog();
    } else {
      const dialog = nativeDrawer?.querySelector('dialog');
      if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
        dialog.showModal();
      }
    }
  }

  // Update the cart count bubble on load and cart changes
  function updateCartBubble() {
    fetch('/cart.js')
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(cart => {
        const cartBubbles = document.querySelectorAll('#cart-bubble, .grillion-header .cart-count, .grillion-cart-count-badge');
        cartBubbles.forEach(bubble => {
          bubble.textContent = cart.item_count;
          bubble.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });

        // Also update any drawer count pills if present
        const drawerPills = document.querySelectorAll('.cart-drawer__count-pill, [id^="GrillionDrawerCount"]');
        drawerPills.forEach(pill => {
          pill.textContent = cart.item_count;
        });
      })
      .catch(err => console.error('Error fetching cart state:', err));
  }

  // Robustly refresh cart drawer contents and re-render items
  let isRefreshing = false;
  async function refreshCartDrawer(openDrawer = true, cartData = null) {
    if (isRefreshing) return;
    isRefreshing = true;

    try {
      const sectionId = getHeaderSectionId();
      let sectionHtml = cartData?.sections?.[sectionId] || cartData?.data?.sections?.[sectionId];

      if (!sectionHtml) {
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&_t=${Date.now()}`;
        const res = await fetch(url);
        if (res.ok) {
          sectionHtml = await res.text();
        }
      }

      if (sectionHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');

        const newInner = doc.querySelector('.cart-drawer__inner');
        const currentInner = document.querySelector('.cart-drawer__inner');
        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }

        const newDialog = doc.querySelector('.cart-drawer__dialog');
        const currentDialog = document.querySelector('.cart-drawer__dialog');
        if (newDialog && currentDialog) {
          if (newDialog.classList.contains('cart-drawer--empty')) {
            currentDialog.classList.add('cart-drawer--empty');
          } else {
            currentDialog.classList.remove('cart-drawer--empty');
          }
        }

        const countPill = doc.querySelector('.cart-drawer__count-pill');
        if (countPill) {
          const count = parseInt(countPill.textContent.trim(), 10) || 0;
          document.querySelectorAll('#cart-bubble, .grillion-header .cart-count, .grillion-cart-count-badge').forEach(b => {
            b.textContent = count;
            b.style.display = count > 0 ? 'flex' : 'none';
          });
          document.querySelectorAll('.cart-drawer__count-pill, [id^="GrillionDrawerCount"]').forEach(p => {
            p.textContent = count;
          });
        }
      }

      updateCartBubble();

      if (openDrawer) {
        openCartDrawer();
      }
    } catch (err) {
      console.error('Error refreshing cart drawer:', err);
      updateCartBubble();
      if (openDrawer) {
        openCartDrawer();
      }
    } finally {
      isRefreshing = false;
    }
  }

  window.refreshCartDrawer = refreshCartDrawer;

  updateCartBubble();

  // Listen for all Shopify cart update events to refresh the drawer and bubble
  document.addEventListener('cart:update', (e) => {
    refreshCartDrawer(false, e?.detail?.data || e?.detail?.resource);
  });
  document.addEventListener('cart:updated', (e) => {
    refreshCartDrawer(false, e?.detail?.data || e?.detail?.resource || e?.detail?.cart);
  });
  document.addEventListener('cart:add', (e) => {
    refreshCartDrawer(true, e?.detail?.data || e?.detail?.resource);
  });
  document.addEventListener('cart:refresh', () => {
    refreshCartDrawer(false);
  });

  // Handle clicking the cart icon to open the cart drawer safely
  const cartIcon = document.getElementById('cart-icon-btn');
  if (cartIcon && !cartIcon.dataset.hasCartListener) {
    cartIcon.dataset.hasCartListener = 'true';
    cartIcon.addEventListener('click', (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  }

  // Fallback add to cart function for standard pages
  window.addShopifyItemToCart = function(variantId, qty = 1, options = {}) {
    if (!variantId) {
      console.warn('Cannot add item: variant ID is missing');
      return;
    }

    const sectionId = getHeaderSectionId();
    const payload = {
      items: [{
        id: parseInt(variantId, 10),
        quantity: parseInt(qty, 10)
      }],
      sections: sectionId
    };

    if (options.upsells && options.upsells.length > 0) {
      options.upsells.forEach(id => {
        payload.items.push({
          id: parseInt(id, 10),
          quantity: 1
        });
      });
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(res => {
      if (!res.ok) throw new Error('Failed to add item to cart');
      return res.json();
    })
    .then(data => {
      refreshCartDrawer(true, data);
    })
    .catch(err => {
      console.error('Failed to add items to Shopify cart:', err);
      alert('Error adding items: ' + err.message);
    });
  };

  document.addEventListener('click', (e) => {
    const cartTrigger = e.target.closest('.add-to-cart-trigger');
    if (cartTrigger && !cartTrigger.closest('.grillion-products-section')) {
      e.preventDefault();
      const variantId = cartTrigger.dataset.variantId;
      if (variantId) {
        window.addShopifyItemToCart(variantId, 1);
      }
    }
  });
});
